/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRecoilState, useRecoilStateLoadable, useRecoilValueLoadable } from "recoil";
import {
  IClassSubscription,
  IUser,
  ProgramData,
  ProgramDataVersionInfo,
  ProgramDataVersions,
} from "@interfaces/Interfaces";
import { programDataAtom, programDataVersionInfoAtom, userAtom, userProgramsAtom } from "@state/State";
import RemoteDataController from "@controllers/RemoteDataController";
import { PF_SERVER_URL, PROGRAM_DATA_REFRESH_INTERVAL } from "../../Constants";
import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebugUser } from "@hooks/auth/useDebugUser";
import * as ProgramUtils from "@utils/programUtils";
import _ from "lodash";
import { EventRegister } from "react-native-event-listeners";
import DataController from "@controllers/DataController";
import * as NotificationService from "@controllers/notificationService";
import { useAppFocus } from "@hooks/app/useAppFocus";
import { useConnectionStatus } from "@hooks/app/useConnectionStatus";

const PROGRAM_DATA_URL: string = PF_SERVER_URL + "/portfolio/git/getProgramsGit";

const PROGRAM_DATA_VERSIONS_URL: string = PF_SERVER_URL + "/portfolio/git/getVersions";

export interface UseProgramData {
  programData: ProgramData[];
  userPrograms: ProgramData[];
  versionInfo: ProgramDataVersions[];
  loadProgramDataForLogin: (user: IUser) => Promise<void>;
  getProgramStartDate: (programID: string, programSubscriptionIDs: string[]) => Date | undefined;
}

export const useProgramData = (loadData?: boolean): UseProgramData => {
  const queryClient = useQueryClient();
  const { isDebugUser } = useDebugUser();
  const connectionStatus = useConnectionStatus();
  const appInForeground = useAppFocus();

  const userObject = useRecoilValueLoadable<IUser | null>(userAtom);
  const [userPrograms, setUserPrograms] = useRecoilState<ProgramData[]>(userProgramsAtom);
  const [programData, setProgramData] = useRecoilStateLoadable<ProgramData[]>(programDataAtom);
  const [versionInfo, setVersionInfo] = useRecoilState<ProgramDataVersions[]>(programDataVersionInfoAtom);

  // const getProgramStartDate = (programID: string, programSubscriptionName: string): Date | undefined => {
  const getProgramStartDate = (programID: string, programSubscriptionIDs: string[]): Date | undefined => {
    const subscriptions = userObject.contents?.userData.subscriptions ?? [];

    const programSubscription: IClassSubscription = subscriptions.find((item: IClassSubscription | any) => {
      const subscriptionPrograms: string[] = item.ParaFolioPrograms;

      return (
        subscriptionPrograms?.some((_sub) => programSubscriptionIDs.includes(_sub)) ||
        programSubscriptionIDs.includes(`${item.SubscriptionID}`)
      );
    });

    if (programSubscription && programSubscription.App_Data) {
      let json: any = {};

      try {
        json = JSON.parse(programSubscription.App_Data);
      } catch (error) {
        console.log(error);
      }

      if (json[`${programID}`]?.startDate) {
        const startDate = new Date(json[`${programID}`]?.startDate);

        return startDate;
      }
    }
    // console.log(programSubscription);
  };

  const fetchProgramData = async (
    user: IUser,
    debug: boolean,
    programIDs?: string[]
  ): Promise<ProgramData[] | null> => {
    try {
      const contactID = user.userData?.contactID;
      const token = user.token;

      const response: Response = await fetch(PROGRAM_DATA_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ContactID: `${contactID}`,
          Token: token,
          debug: debug ?? false,
          ...(programIDs && { programs: programIDs }),
        }),
      });

      const json = await response.json();

      return json.success ? json.body ?? [] : null;
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      return null;
    }
  };

  const fetchProgramVersions = async (user: IUser, debug: boolean): Promise<ProgramDataVersionInfo[] | null> => {
    try {
      const contactID = user.userData?.contactID;
      const token = user.token;

      const response: Response = await fetch(PROGRAM_DATA_VERSIONS_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ContactID: `${contactID}`,
          Token: token,
          debug: debug ?? false,
        }),
      });

      const json = await response.json();

      return json.success ? json.body : null;
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      return null;
    }
  };

  const hasProgramUpdates = (
    currentVersions: ProgramDataVersionInfo[],
    otaVersions: ProgramDataVersionInfo[]
  ): boolean => {
    let result = false;

    const length = otaVersions.length;

    for (let i = 0; i < length; i++) {
      const otaVersion = otaVersions[i];
      const currentVersion = currentVersions.find((item) => item.ID === otaVersion.ID);

      if (currentVersion && otaVersion.version !== currentVersion.version) {
        result = true;
        break;
      }
    }

    return result;
  };

  const checkUserHasProgramAccess = useCallback((): boolean => {
    let result = false;

    if (userObject.contents) {
      const allSubscriptions: IClassSubscription[] = userObject.contents.userData?.subscriptions ?? [];
      const length = allSubscriptions.length;

      for (let i = 0; i < length; i++) {
        const subscription = allSubscriptions[i];
        const subscriptionID = subscription.SubscriptionID;
        const subscriptionPrograms = subscription.ParaFolioPrograms;

        if (subscriptionID === 1531) {
          result = true;
          break;
        }

        if (subscriptionPrograms && subscriptionPrograms.length > 0) {
          result = true;
          break;
        }
      }
    }

    return result;
  }, [userObject.contents]);

  const getProgramsForUser = useCallback(async (user: IUser, programs: ProgramData[]): Promise<ProgramData[]> => {
    try {
      let array = [];
      const allSubscriptions: IClassSubscription[] = user.userData?.subscriptions ?? [];

      const length = allSubscriptions.length;

      for (let i = 0; i < length; i++) {
        const subscription = allSubscriptions[i];
        const subscriptionID = subscription.SubscriptionID;
        const subscriptionPrograms = subscription.ParaFolioPrograms;

        const programList = programs.filter((item) => {
          // Subscription field in Program table - refers to subscription ID or ParaFolioPrograms name
          if (item.Subscription) {
            if (subscriptionPrograms && subscriptionPrograms?.length > 0) {
              return subscriptionPrograms.some((_sub) => item.Subscription?.includes(_sub));
            } else if (item.Subscription.includes(`${subscriptionID}`)) {
              return true;
            }
          }

          return false;
        });

        if (programList.length > 0) {
          array.push(...programList);
        }
      }

      array = _.uniqBy(array, "ID");
      return array;
    } catch (error) {
      console.log(error);
      return [];
    }
  }, []);

  const getVersionInfo = useCallback(async (user: IUser, data: ProgramData[]): Promise<ProgramDataVersions[]> => {
    const currentVersions = ProgramUtils.mapProgramDataVersionInfo(data);
    const [otaVersions, releaseVersions] = await Promise.all([
      fetchProgramVersions(user, true),
      fetchProgramVersions(user, false),
    ]);

    return [
      { type: "User", versionInfo: currentVersions },
      { type: "Release", versionInfo: releaseVersions ?? [] },
      { type: "Debug", versionInfo: otaVersions ?? [] },
    ];
  }, []);

  const user = userObject.contents;
  const contactID = user?.userData?.contactID;
  const hasProgramAccess = checkUserHasProgramAccess();
  const isEnabled = Boolean(
    loadData &&
      user &&
      user.userData &&
      connectionStatus?.connected !== false &&
      hasProgramAccess &&
      userObject.state === "hasValue" &&
      programData.state === "hasValue"
  );

  // Program Data Query
  const fetchProgramDataWithSideEffects = useCallback(async (): Promise<ProgramData[]> => {
    if (!user) {
      throw new Error("User is required");
    }

    const currentProgramData = programData.contents || [];
    const isEmpty = currentProgramData.length === 0;
    let shouldFetch = isEmpty;

    if (!isEmpty) {
      // Check versions
      const currentVersions = ProgramUtils.mapProgramDataVersionInfo(currentProgramData);
      const otaVersions = await fetchProgramVersions(user, isDebugUser);

      if (otaVersions) {
        shouldFetch = hasProgramUpdates(currentVersions, otaVersions);
      }
    }

    if (!shouldFetch) {
      // Still update version info even if we don't fetch
      const versionInfoData = await getVersionInfo(user, currentProgramData);
      setVersionInfo(versionInfoData);
      appInForeground && EventRegister.emitEvent("ota/update-completed");
      return currentProgramData;
    }

    // Fetch program data
    const isFirstLoad = isEmpty;
    appInForeground && EventRegister.emitEvent("ota/update-started", isFirstLoad);

    try {
      const data = await fetchProgramData(user, isDebugUser);

      if (!data || data.length === 0) {
        appInForeground && EventRegister.emitEvent("ota/update-completed");
        return currentProgramData;
      }

      // Run side effects
      const userProgramsData = await getProgramsForUser(user, data);
      const versionInfoData = await getVersionInfo(user, data);

      // Update atoms (only update userPrograms if changed)
      setProgramData(data);
      const isEqual = _.isEqual(userPrograms, userProgramsData);
      if (!isEqual) {
        setUserPrograms(userProgramsData);
      }
      setVersionInfo(versionInfoData);

      appInForeground && EventRegister.emitEvent("ota/update-completed", true);
      return data;
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      appInForeground && EventRegister.emitEvent("ota/update-errored");
      throw error;
    }
  }, [user, isDebugUser, programData.contents, appInForeground, getProgramsForUser, getVersionInfo, userPrograms]);

  const { data: fetchedProgramData } = useQuery({
    queryKey: ["program-data", contactID, isDebugUser],
    queryFn: fetchProgramDataWithSideEffects,
    enabled: isEnabled,
    refetchInterval: isEnabled ? PROGRAM_DATA_REFRESH_INTERVAL : false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  // Version Info Query (can be used independently)
  const { data: fetchedVersionInfo } = useQuery({
    queryKey: ["program-version-info", contactID],
    queryFn: async () => {
      if (!user) {
        throw new Error("User is required");
      }
      const currentProgramData = programData.contents || [];
      return await getVersionInfo(user, currentProgramData);
    },
    enabled: Boolean(user && programData.state === "hasValue" && programData.contents),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const lastSubscribedTopicsRef = useRef<string>("");

  useEffect(() => {
    if (user?.userData?.contactID && userPrograms.length > 0 && !DataController.isWeb()) {
      const topicsString = [user?.userData?.contactID, ...userPrograms.map((p) => p.ID).sort()]
        .filter(Boolean)
        .sort()
        .join(",");

      if (topicsString !== lastSubscribedTopicsRef.current) {
        lastSubscribedTopicsRef.current = topicsString;
        NotificationService.subscribeToTopics(user, userPrograms).catch((error) => {
          console.log("Error subscribing to notification topics:", error);
        });
      }
    }
  }, [user?.userData?.contactID]);

  useEffect(() => {
    if (fetchedVersionInfo) {
      setVersionInfo(fetchedVersionInfo);
    }
  }, [fetchedVersionInfo, setVersionInfo]);

  // Replace ota/load-data event listener with query invalidation
  useEffect(() => {
    const handleLoadData = () => {
      if (contactID) {
        queryClient.invalidateQueries({ queryKey: ["program-data", contactID] });
      }
    };

    EventRegister.addEventListener("ota/load-data", handleLoadData);

    return () => {
      EventRegister.removeEventListener("ota/load-data");
    };
  }, [contactID, queryClient]);

  // Manual refresh function for login
  const loadProgramDataForLogin = useCallback(
    async (loginUser: IUser): Promise<void> => {
      if (!loginUser) return;

      const allSubscriptions: IClassSubscription[] = loginUser.userData?.subscriptions ?? [];

      const hasPrograms = allSubscriptions.some((subscription) => {
        const subscriptionID = subscription.SubscriptionID;
        const subscriptionPrograms = subscription.ParaFolioPrograms;
        return subscriptionID === 1531 || (subscriptionPrograms && subscriptionPrograms.length > 0);
      });

      if (!hasPrograms) return;

      try {
        const loginContactID = loginUser.userData?.contactID;
        if (loginContactID) {
          // Use fetchQuery to ensure we wait for the data to load, even if query is disabled
          await queryClient.fetchQuery({
            queryKey: ["program-data", loginContactID, false],
            queryFn: async (): Promise<ProgramData[]> => {
              // Create a version of fetchProgramDataWithSideEffects that uses loginUser
              const currentProgramData = programData.contents || [];
              const isEmpty = currentProgramData.length === 0;
              let shouldFetch = isEmpty;

              if (!isEmpty) {
                // Check versions
                const currentVersions = ProgramUtils.mapProgramDataVersionInfo(currentProgramData);
                const otaVersions = await fetchProgramVersions(loginUser, isDebugUser);

                if (otaVersions) {
                  shouldFetch = hasProgramUpdates(currentVersions, otaVersions);
                }
              }

              if (!shouldFetch) {
                // Still update version info even if we don't fetch
                const versionInfoData = await getVersionInfo(loginUser, currentProgramData);
                setVersionInfo(versionInfoData);
                return currentProgramData;
              }

              // Fetch program data
              const data = await fetchProgramData(loginUser, isDebugUser);

              if (!data || data.length === 0) {
                return currentProgramData;
              }

              // Run side effects
              const userProgramsData = await getProgramsForUser(loginUser, data);
              const versionInfoData = await getVersionInfo(loginUser, data);

              // Update atoms (only update userPrograms if changed)
              setProgramData(data);
              const isEqual = _.isEqual(userPrograms, userProgramsData);
              if (!isEqual) {
                setUserPrograms(userProgramsData);
              }
              setVersionInfo(versionInfoData);

              return data;
            },
          });
        }
      } catch (error: any) {
        console.log(error);
        RemoteDataController.logError(error);
      }
    },
    [queryClient, programData.contents, isDebugUser, getProgramsForUser, getVersionInfo, userPrograms]
  );

  return {
    programData: (fetchedProgramData || programData.contents) as ProgramData[],
    userPrograms,
    versionInfo,
    loadProgramDataForLogin,
    getProgramStartDate,
  };
};
